<script setup lang="ts">
import type { LocationInput } from '@wilson/shared'
import { ref } from 'vue'

const model = defineModel<LocationInput | undefined>()
const mode = ref<'device' | 'manual'>('device')
const locating = ref(false)
const error = ref('')
const publicArea = ref('')
const manualDescription = ref('')

function syncManual() {
  if (!manualDescription.value.trim()) {
    model.value = undefined
    return
  }
  model.value = {
    source: 'manual',
    description: manualDescription.value,
    ...(publicArea.value.trim() ? { publicArea: publicArea.value } : {}),
  }
}

function syncPublicArea() {
  if (model.value?.source === 'device') {
    model.value = {
      ...model.value,
      ...(publicArea.value.trim() ? { publicArea: publicArea.value } : {}),
    }
  } else {
    syncManual()
  }
}

function useLocation() {
  error.value = ''
  if (!navigator.geolocation) {
    error.value = 'This browser cannot provide a device location. You can enter it manually.'
    return
  }
  locating.value = true
  navigator.geolocation.getCurrentPosition(
    (position) => {
      model.value = {
        source: 'device',
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        ...(publicArea.value.trim() ? { publicArea: publicArea.value } : {}),
      }
      locating.value = false
    },
    () => {
      error.value =
        'Location was not available. Nothing was submitted; you can enter a place instead.'
      locating.value = false
    },
    { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
  )
}
</script>

<template>
  <fieldset class="location-field">
    <legend>Location</legend>
    <p class="field-help">Exact device locations are encrypted and never shown publicly.</p>
    <div class="segmented">
      <button type="button" :class="{ active: mode === 'device' }" @click="mode = 'device'">
        Device location
      </button>
      <button type="button" :class="{ active: mode === 'manual' }" @click="mode = 'manual'">
        Enter manually
      </button>
    </div>
    <template v-if="mode === 'device'">
      <button class="button secondary" type="button" :disabled="locating" @click="useLocation">
        {{
          locating
            ? 'Finding your location…'
            : model?.source === 'device'
              ? 'Location captured ✓'
              : 'Use my current location'
        }}
      </button>
    </template>
    <label v-else>
      Private location description
      <textarea
        v-model="manualDescription"
        rows="2"
        placeholder="Near the woodland entrance"
        @input="syncManual"
      />
    </label>
    <label>
      Public town or region <span class="optional">optional</span>
      <input
        v-model="publicArea"
        placeholder="Grantham, Lincolnshire"
        maxlength="160"
        @input="syncPublicArea"
      />
    </label>
    <p v-if="error" class="form-error" role="alert">{{ error }}</p>
  </fieldset>
</template>
